import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'
import { ContentStateProperty, DropdownOption, DropdownValue, LocalizedDropdownOptions } from '../../../../@types/types'
import { InputLabel } from '../../display/labelText/LabelText'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import {
  Dropdown,
  DropdownConfirmationButtonItem,
  DropdownList,
  MultiSelectDropdownItem,
  TextInput,
} from 'is24-corecss'
import {
  buildDropdownOptions,
  formHasErrors,
  getNewStyles,
  handleEmpty,
  isEqualTo,
  isNoInformation,
  isValueInvalid,
  optionToValue,
} from '../dropdowns/commonUtils'
import styles from './MultiSelectDropdown.module.css'
import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll'

export type MultiSelectDropdownProps = {
  id: string
  name: ContentStateProperty
  label?: string
  labelClassName?: string
  ariaLabel?: string
  disabled?: boolean
  values?: DropdownValue[]
  noInformation?: string
  style?: React.CSSProperties
  options: DropdownOption[] | LocalizedDropdownOptions
  placeholder?: string
  allowEmpty?: boolean
  errors?: string[]
  onChange: (value: any) => void
  onBlur?: () => void
  onBlurTracking?: (value: any, isUnchanged: boolean) => void
  onFocus?: () => void
}

const notNull = (o: DropdownOption) => o.value != null

const removeNoInformation = (o: DropdownOption) => o.value !== 'NO_INFORMATION'

const valueToOption = (dropdownOptions: DropdownOption[], values: DropdownValue[] | undefined): DropdownOption[] =>
  !values
    ? dropdownOptions.filter((option) => option.value === handleEmpty(values))
    : dropdownOptions.filter((option) => values.includes(option.value))

const MultiSelectDropdown = ({
  id,
  label,
  labelClassName,
  name,
  ariaLabel,
  disabled,
  values,
  noInformation,
  style,
  options,
  placeholder,
  allowEmpty,
  errors = [],
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
}: MultiSelectDropdownProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const isFieldMandatory = useIsRequiredProperty(name)
  const triggerInputRef = useRef<HTMLInputElement>(null)

  const dropdownOptions: DropdownOption[] = useMemo(
    () => buildDropdownOptions(isFieldMandatory, options, t, noInformation),
    [isFieldMandatory, noInformation, options, t]
  )
  const [highlightValidation, setHighlightValidation] = useState(false)
  const [isUnchanged, setIsUnchanged] = useState(true)
  const selectedOptions = useMemo(() => valueToOption(dropdownOptions, values), [dropdownOptions, values])
  const selectedOptionsRef = useRef(selectedOptions)
  const [previousOptions, setPreviousOptions] = useState(selectedOptions)
  const [currentOptions, setCurrentOptions] = useState(selectedOptions)

  const [open, setOpen] = React.useState(false)
  const handleClose = () => {
    onLostFocus()
    setOpen(false)
  }
  useDisableBodyScroll(open, { mobileOnly: true })

  const hasErrors = formHasErrors(errors, formState, name)
  const isInvalid = isValueInvalid(
    currentOptions.filter(removeNoInformation).map(optionToValue),
    highlightValidation,
    allowEmpty,
    hasErrors
  )
  const inputStyles = getNewStyles(style, highlightValidation, values, isInvalid)

  const resetTriggerInputScrollPosition = useCallback(() => {
    const input = triggerInputRef.current

    if (!input) {
      return
    }

    input.scrollLeft = 0
    input.setSelectionRange?.(0, 0)
  }, [])

  const scheduleTriggerInputScrollReset = useCallback(() => {
    requestAnimationFrame(() => {
      resetTriggerInputScrollPosition()
    })
  }, [resetTriggerInputScrollPosition])

  useEffect(() => {
    values && setHighlightValidation(true)
    if (!isEqualTo(selectedOptionsRef.current, selectedOptions)) {
      setCurrentOptions(selectedOptions)
      selectedOptionsRef.current = selectedOptions
    }
  }, [selectedOptions, values])

  useEffect(() => {
    scheduleTriggerInputScrollReset()
  }, [open, scheduleTriggerInputScrollReset, values])

  useEffect(() => {
    requestAnimationFrame(() => {
      const input = triggerInputRef.current

      if (!input || open) {
        return
      }

      if (document.activeElement === input) {
        input.blur()
      }

      resetTriggerInputScrollPosition()
    })
  }, [open, resetTriggerInputScrollPosition])

  const handleFocus = () => {
    scheduleTriggerInputScrollReset()
    onFocus?.()
  }

  const handleOpen = () => {
    setOpen((prev) => !prev)
    scheduleTriggerInputScrollReset()
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
    event.preventDefault()
    handleOpen()
  }

  const onLostFocus = () => {
    onBlur && onBlur()
    if (!isEqualTo(previousOptions, currentOptions)) {
      onBlurTracking && onBlurTracking(currentOptions, isUnchanged)
      isUnchanged && setIsUnchanged(false)
      setPreviousOptions(currentOptions)
    }
  }

  const isSelected = (item: DropdownOption) => currentOptions.map(optionToValue).includes(item.value)

  const onSelectChange = (newValue: DropdownOption[]) => {
    setHighlightValidation(true)
    onChange(newValue.map(optionToValue))
    setTouched(name)
  }

  function handleNoInformation(selectedOption: DropdownOption): DropdownOption[] {
    return [selectedOption]
  }

  function handleRegularSelect(selectedOption: DropdownOption): DropdownOption[] {
    if (!isSelected(selectedOption)) {
      return [...[...currentOptions].filter(notNull).filter(removeNoInformation), selectedOption]
    } else {
      return currentOptions.filter((o) => o.value !== selectedOption.value)
    }
  }

  const selectItem = (selectedOption: DropdownOption) => {
    let newValue

    if (isNoInformation(selectedOption)) {
      newValue = handleNoInformation(selectedOption)
    } else {
      newValue = handleRegularSelect(selectedOption)
    }

    setCurrentOptions(newValue)
    onSelectChange(newValue)
  }

  const dropdownValue = () => {
    return dropdownOptions
      .filter((o) => o.value && currentOptions.map((item) => item.value).includes(o.value))
      .map((o) => o.label)
      .join(', ')
  }

  const isVisiblePalm = `desk-hide lap-hide ${open ? '' : ' palm-hide'}`

  return (
    <>
      <div data-testid={`${id}-palm-backdrop`} className={`${isVisiblePalm} ${styles.palmBackdrop}`} />
      <div id={`${id}-select-container`} data-testid={`${id}-select-container`} className='select-container'>
        {label && <InputLabel id={id} labelTranslationKey={label} propertyName={name} labelClass={labelClassName} />}
        <div>
          <Dropdown isOpen={open} onClose={handleClose} className='select-input-wrapper one-whole'>
            <TextInput.Root
              id={id}
              data-testid={`${id}-select-text-input`}
              invalid={isInvalid}
              style={inputStyles}
              {...(disabled && { disabled: true })}
              {...(ariaLabel && { 'aria-label': ariaLabel })}
            >
              <TextInput.Input
                ref={triggerInputRef}
                className={`${styles.triggerInput} font-ellipsis`}
                value={dropdownValue()}
                onMouseDown={handleMouseDown}
                onFocus={handleFocus}
                placeholder={placeholder ? placeholder : ''}
                readOnly
              />
            </TextInput.Root>
            {open && (
              <DropdownList className={styles.dropdownList} isCondensed={true}>
                {dropdownOptions
                  .filter((o) => o.value != null && o.label)
                  .map((option, index) => (
                    <MultiSelectDropdownItem
                      id={`${index}-${option.value}`}
                      key={index}
                      selected={isSelected(option)}
                      onClick={() => !disabled && selectItem(option)}
                      value={option?.value?.toString()}
                    >
                      {option.label!.toString()}
                    </MultiSelectDropdownItem>
                  ))}
                <DropdownConfirmationButtonItem
                  className={'one-whole'}
                  onClick={handleClose}
                  label={t('wizard.screen.construction.save')}
                />
              </DropdownList>
            )}
          </Dropdown>
        </div>
        {isInvalid && hasErrors() && (
          <div role='alert' className='font-error' aria-labelledby={id + '-input-label'}>
            <div className='caption caption--error'>
              <IconS24CrossCircle24Filled className={'caption-icon'} />
              {t(`validation.${errors[0]}`)}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default MultiSelectDropdown
